import React, { useState, useMemo, useEffect } from 'react'
import { Modal, Table, Button, InputNumber, Input, message } from 'antd'
import moment from 'moment'
import {
  saveAeciHourlyEvents,
  deleteAeciHourlyEvents,
  validateAndUpdateAeciAvailCap,
  moveAeciHourlyEvent,
} from '../aeciApi'
import { ZERO_CAP_TYPES, DERATE_TYPES } from './eventTypeRules'
import MoveEventModal from './MoveEventModal'

const SAVE_FORMAT = 'MM/DD/YYYY HH:mm'
const LOCKED = new Set(['S', 'L', 'ML'])
const ALL_MINUTES = Array.from({ length: 60 }, (_, i) => i)

const HourEventsModal = ({
  visible,
  onClose,
  onSaved,
  hour,
  eventRow,
  selectedDate,
  selectedUnit,
  apiTargetInfo,
  hasWrite,
  monthlyLockStatus,
  unitHourlyRows,
  eventTypes,
  causeCodeList,
}) => {
  const [addMinute, setAddMinute] = useState(0)
  const [addAvailCap, setAddAvailCap] = useState(null)
  const [derate, setDerate] = useState(null)
  const [saving, setSaving] = useState(false)
  const [editingCaps, setEditingCaps] = useState({})
  const [savingCapNum, setSavingCapNum] = useState(null)
  const [addError, setAddError] = useState(null)
  const [conflictInfo, setConflictInfo] = useState(null)
  const [moveEvent, setMoveEvent] = useState(null)
  const [moving, setMoving] = useState(false)

  useEffect(() => {
    if (!visible) {
      setEditingCaps({})
      setAddMinute(0)
      setAddAvailCap(null)
      setDerate(null)
      setSaving(false)
      setSavingCapNum(null)
      setAddError(null)
      setConflictInfo(null)
      setMoveEvent(null)
      setMoving(false)
    }
  }, [visible])

  useEffect(() => {
    setAddError(null)
  }, [addMinute, addAvailCap, derate])

  const events = useMemo(() => {
    if (!eventRow || !eventRow.eventDataMap || !hour) return []
    return (eventRow.eventDataMap[hour] || [])
      .slice()
      .sort(
        (a, b) => moment(a.eventStart).minute() - moment(b.eventStart).minute()
      )
  }, [eventRow, hour])

  const occupiedMinutes = useMemo(
    () => new Set(events.map(e => moment(e.eventStart).minute())),
    [events]
  )

  const eventType =
    eventRow && eventRow.eventData && eventRow.eventData.eventType
  const causeCode =
    eventRow && eventRow.eventData && eventRow.eventData.causeCode
  const isZeroCap = ZERO_CAP_TYPES.has(eventType)
  const isDerate = DERATE_TYPES.has(eventType) && !isZeroCap

  const handleAdd = () => {
    if (monthlyLockStatus === 'ML') {
      setAddError('Month is locked')
      return
    }
    if (
      !isZeroCap &&
      previewUnalloc != null &&
      Math.round(previewUnalloc * 10) / 10 < 0
    ) {
      setAddError(
        `Adding this event would over-allocate H${String(hour).padStart(
          2,
          '0'
        )} ` +
          `(New weighted: ${
            previewWeightedSum != null ? previewWeightedSum.toFixed(1) : '?'
          }, ` +
          `Unallocated would be: ${previewUnalloc.toFixed(1)})`
      )
      return
    }
    const utility = selectedUnit ? selectedUnit.substring(0, 3) : ''
    const unit = selectedUnit ? selectedUnit.substring(3) : ''
    const eventStart = selectedDate
      .clone()
      .hour(hour - 1)
      .minute(addMinute)
      .second(0)
      .format(SAVE_FORMAT)
    const payload = [
      {
        utility,
        unit,
        eventNum: -1,
        eventType,
        eventStart,
        availCap: isZeroCap
          ? 0
          : isDerate
          ? unalloc != null && derate != null
            ? Math.round((unalloc - derate) * 100) / 100
            : null
          : addAvailCap ?? null,
        causeCode: causeCode || 0,
        coc: null,
        description: null,
        isSubmitted: null,
      },
    ]
    setSaving(true)
    saveAeciHourlyEvents(apiTargetInfo)(payload).fork(
      () => {
        setSaving(false)
        setAddError('Save failed')
      },
      result => {
        setSaving(false)
        if (Array.isArray(result) && result.length > 0) {
          setAddError((result[0] && result[0].error) || 'Save failed')
          return
        }
        setAddMinute(0)
        setAddAvailCap(null)
        setAddError(null)
        message.success('Saved')

        onSaved()
      }
    )
  }

  const handleUpdateCap = ev => {
    if (monthlyLockStatus === 'ML') {
      message.error('Month is locked')
      return
    }
    const utility = selectedUnit ? selectedUnit.substring(0, 3) : ''
    const unit = selectedUnit ? selectedUnit.substring(3) : ''
    const payload = {
      utility,
      unit,
      eventType,
      causeCode: causeCode || 0,
      eventNum: ev.eventNum,
      oldAvailCap: ev.availCap != null ? ev.availCap : null,
      newAvailCap:
        editingCaps[ev.eventNum] != null ? editingCaps[ev.eventNum] : null,
    }
    setSavingCapNum(ev.eventNum)
    validateAndUpdateAeciAvailCap(apiTargetInfo)(payload).fork(
      () => {
        setSavingCapNum(null)
        message.error('Save failed')
      },
      result => {
        setSavingCapNum(null)
        if (result.conflict) {
          setConflictInfo({
            eventNum: ev.eventNum,
            dbValue: result.dbValue != null ? Number(result.dbValue) : null,
            enteredValue: editingCaps[ev.eventNum],
          })
          return
        }
        if (result.errorMsg) {
          message.error(result.errorMsg)
          return
        }
        setEditingCaps(prev => {
          const next = { ...prev }
          delete next[ev.eventNum]
          return next
        })
        message.success('Saved')
        onSaved()
      }
    )
  }

  const handleDelete = ev => {
    if (monthlyLockStatus === 'ML') {
      message.error('Month is locked')
      return
    }
    const payload = [
      {
        utility: ev.utility,
        unit: ev.unit,
        eventNum: ev.eventNum,
        eventType: ev.eventType,
        eventStart: moment(ev.eventStart).format(SAVE_FORMAT),
        availCap: ev.availCap != null ? ev.availCap : null,
        causeCode: ev.causeCode || 0,
        coc: ev.coc || null,
        description: ev.description || null,
        isSubmitted: ev.isSubmitted || null,
      },
    ]
    setSaving(true)
    deleteAeciHourlyEvents(apiTargetInfo)(payload).fork(
      () => {
        setSaving(false)
        message.error('Delete failed')
      },
      result => {
        setSaving(false)
        if (Array.isArray(result) && result.length > 0) {
          message.error((result[0] && result[0].error) || 'Delete failed')
          return
        }
        message.success('Deleted')
        onSaved()
      }
    )
  }

  const handleMove = (newEventType, newCauseCode) => {
    const ev = moveEvent
    if (!ev) return
    const payload = {
      event: {
        utility: ev.utility,
        unit: ev.unit,
        eventNum: ev.eventNum,
        eventType: ev.eventType,
        eventStart: moment(ev.eventStart).format(SAVE_FORMAT),
        availCap: ev.availCap != null ? ev.availCap : null,
        causeCode: ev.causeCode || 0,
        coc: ev.coc || null,
        description: ev.description || null,
        isSubmitted: ev.isSubmitted || null,
      },
      newEventType,
      newCauseCode: newCauseCode || 0,
    }
    setMoving(true)
    moveAeciHourlyEvent(apiTargetInfo)(payload).fork(
      () => {
        setMoving(false)
        message.error('Move failed')
      },
      result => {
        setMoving(false)
        if (Array.isArray(result) && result.length > 0) {
          message.error((result[0] && result[0].error) || 'Move failed')
          return
        }
        setMoveEvent(null)
        message.success('Moved')
        onSaved()
      }
    )
  }

  const handleUseDbValue = () => {
    const { eventNum } = conflictInfo
    setEditingCaps(prev => {
      const next = { ...prev }
      delete next[eventNum]
      return next
    })
    setConflictInfo(null)
    onSaved()
  }

  const handleUseMyValue = () => {
    const { eventNum, dbValue, enteredValue } = conflictInfo
    const utility = selectedUnit ? selectedUnit.substring(0, 3) : ''
    const unit = selectedUnit ? selectedUnit.substring(3) : ''
    const payload = {
      utility,
      unit,
      eventType,
      causeCode: causeCode || 0,
      eventNum,
      oldAvailCap: dbValue,
      newAvailCap: enteredValue,
    }
    setSavingCapNum(eventNum)
    validateAndUpdateAeciAvailCap(apiTargetInfo)(payload).fork(
      () => {
        setSavingCapNum(null)
        message.error('Save failed')
        setConflictInfo(null)
      },
      result => {
        setSavingCapNum(null)
        if (result.conflict) {
          message.error('Value changed again — please close and reopen.')
          setConflictInfo(null)
          return
        }
        if (result.errorMsg) {
          message.error(result.errorMsg)
          setConflictInfo(null)
          return
        }
        setEditingCaps(prev => {
          const next = { ...prev }
          delete next[eventNum]
          return next
        })
        setConflictInfo(null)
        message.success('Saved')
        onSaved()
      }
    )
  }

  const columns = [
    {
      title: 'Start Time',
      dataIndex: 'eventStart',
      render: v => (v ? moment(v).format('HH:mm') : '-'),
    },
    {
      title: 'AvailCap',
      dataIndex: 'availCap',
      render: (v, record) => {
        if (hasWrite && !LOCKED.has(record.isSubmitted)) {
          const editVal = editingCaps[record.eventNum]
          return (
            <InputNumber
              value={
                editVal !== undefined ? editVal : v != null ? Number(v) : null
              }
              onChange={val =>
                setEditingCaps(prev => ({ ...prev, [record.eventNum]: val }))
              }
              size="small"
              style={{ width: 90 }}
              disabled={isZeroCap || saving || savingCapNum != null}
            />
          )
        }
        return v != null ? Number(v).toFixed(1) : '-'
      },
    },
    {
      title: 'Weighted',
      key: 'weighted',
      render: (_, record) => {
        const w = weightedByEventNum[record.eventNum]
        return w != null ? w.toFixed(1) : '-'
      },
    },
    ...(hasWrite
      ? [
          {
            title: '',
            key: 'actions',
            render: (_, record) => (
              <div style={{ display: 'flex', gap: 4 }}>
                {!LOCKED.has(record.isSubmitted) && (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleUpdateCap(record)}
                    disabled={
                      isZeroCap ||
                      saving ||
                      savingCapNum != null ||
                      editingCaps[record.eventNum] === undefined
                    }
                    loading={savingCapNum === record.eventNum}
                  >
                    Save
                  </Button>
                )}
                <Button
                  size="small"
                  type="danger"
                  onClick={() => handleDelete(record)}
                  disabled={
                    saving ||
                    savingCapNum != null ||
                    LOCKED.has(record.isSubmitted)
                  }
                >
                  Delete
                </Button>
                {!LOCKED.has(record.isSubmitted) && (
                  <Button
                    size="small"
                    onClick={() => setMoveEvent(record)}
                    disabled={saving || savingCapNum != null}
                  >
                    Move
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ]

  const title = `H${String(hour).padStart(
    2,
    '0'
  )} — ${eventType} (${causeCode})`

  const unalloc = useMemo(() => {
    if (!hour || !unitHourlyRows) return null
    const row = unitHourlyRows.find(r => r.metric === 'Unallocated')
    if (!row) return null
    const v = parseFloat(row[`h${hour}`])
    return isNaN(v) ? null : v
  }, [hour, unitHourlyRows])

  const { weightedSum, weightedByEventNum } = useMemo(() => {
    if (!events || !events.length)
      return { weightedSum: null, weightedByEventNum: {} }
    let total = 0
    const map = {}
    events.forEach((e, i) => {
      const minStart = moment(e.eventStart).minute()
      const minEnd =
        i < events.length - 1 ? moment(events[i + 1].eventStart).minute() : 60
      const cap = e.availCap != null ? parseFloat(e.availCap) : 0
      const w = cap * ((minEnd - minStart) / 60)
      map[e.eventNum] = w
      total += w
    })
    return { weightedSum: total, weightedByEventNum: map }
  }, [events])

  // Preview weighted sum as if the new event (addMinute, addAvailCap) were inserted.
  const computedAvailCap =
    isDerate && unalloc != null && derate != null
      ? Math.round((unalloc - derate) * 100) / 100
      : null
  const effectiveAddAvailCap = isZeroCap
    ? 0
    : isDerate
    ? computedAvailCap
    : addAvailCap

  const previewWeightedSum = useMemo(() => {
    if (isZeroCap || effectiveAddAvailCap == null || !hour || !selectedDate)
      return null
    const newMinStart = typeof addMinute === 'number' ? addMinute : 0
    const preview = {
      _preview: true,
      eventStart: moment()
        .minute(newMinStart)
        .toDate(),
      availCap: effectiveAddAvailCap,
    }
    const combined = [...events, preview].sort(
      (a, b) => moment(a.eventStart).minute() - moment(b.eventStart).minute()
    )
    let total = 0
    combined.forEach((e, i) => {
      const minStart = moment(e.eventStart).minute()
      const minEnd =
        i < combined.length - 1
          ? moment(combined[i + 1].eventStart).minute()
          : 60
      const cap = e.availCap != null ? parseFloat(e.availCap) : 0
      total += cap * ((minEnd - minStart) / 60)
    })
    return total
  }, [isZeroCap, effectiveAddAvailCap, addMinute, events, hour, selectedDate])

  const previewUnalloc = useMemo(() => {
    if (previewWeightedSum == null || unalloc == null) return null
    const currentW = weightedSum ?? 0
    return unalloc - (previewWeightedSum - currentW)
  }, [previewWeightedSum, unalloc, weightedSum])

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      {(unalloc != null || weightedSum != null) && (
        <div
          style={{ marginBottom: 8, fontSize: 12, display: 'flex', gap: 16 }}
        >
          {weightedSum != null && (
            <span>
              <span style={{ color: '#8c8c8c' }}>Weighted: </span>
              <span style={{ fontWeight: 600 }}>{weightedSum.toFixed(1)}</span>
            </span>
          )}
          {unalloc != null && (
            <span>
              <span style={{ color: '#8c8c8c' }}>Unallocated: </span>
              <span
                style={{
                  fontWeight: 600,
                  color: unalloc < 0 ? '#f5222d' : '#52c41a',
                }}
              >
                {unalloc.toFixed(1)}
              </span>
            </span>
          )}
        </div>
      )}
      {conflictInfo && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 4,
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 12 }}>
            DB value:{' '}
            <strong>
              {conflictInfo.dbValue != null
                ? Number(conflictInfo.dbValue).toFixed(1)
                : 'null'}
            </strong>
            {' — '}Your value:{' '}
            <strong>
              {conflictInfo.enteredValue != null
                ? Number(conflictInfo.enteredValue).toFixed(1)
                : 'null'}
            </strong>
          </span>
          <Button size="small" onClick={handleUseDbValue}>
            Use DB value
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={handleUseMyValue}
            loading={savingCapNum === conflictInfo.eventNum}
            disabled={
              savingCapNum != null && savingCapNum !== conflictInfo.eventNum
            }
          >
            Save my value
          </Button>
        </div>
      )}
      <Table
        dataSource={events}
        columns={columns}
        rowKey={r => r.eventNum}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No events for this hour' }}
      />

      {events.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: '#595959', marginBottom: 4 }}>
            Description
          </div>
          <Input.TextArea
            value={events[0].description || ''}
            disabled
            rows={2}
            style={{ resize: 'none', color: '#333', background: '#fafafa' }}
          />
        </div>
      )}

      {hasWrite && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 6, fontSize: 12, color: '#595959' }}>
            Select minute —{' '}
            <span style={{ color: '#52c41a', fontWeight: 600 }}>selected</span>{' '}
            · <span style={{ color: '#096dd9' }}>has event</span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, 1fr)',
              gap: 3,
              marginBottom: 12,
            }}
          >
            {ALL_MINUTES.map(m => {
              const isSelected = addMinute === m
              const hasEvent = occupiedMinutes.has(m)
              return (
                <div
                  key={m}
                  onClick={() => !saving && setAddMinute(m)}
                  style={{
                    padding: '4px 2px',
                    textAlign: 'center',
                    cursor: saving ? 'default' : 'pointer',
                    borderRadius: 3,
                    fontSize: 11,
                    fontWeight: isSelected ? 700 : 400,
                    backgroundColor: isSelected
                      ? '#52c41a'
                      : hasEvent
                      ? '#e6f7ff'
                      : '#f5f5f5',
                    color: isSelected ? '#fff' : hasEvent ? '#096dd9' : '#333',
                    border: isSelected
                      ? '1px solid #389e0d'
                      : hasEvent
                      ? '1px solid #91d5ff'
                      : '1px solid #d9d9d9',
                    userSelect: 'none',
                  }}
                >
                  {String(m).padStart(2, '0')}
                </div>
              )
            })}
          </div>
          {isDerate && unalloc != null && (
            <div style={{ fontSize: 12, color: '#52c41a', marginBottom: 4 }}>
              {`Unallocated: ${unalloc.toFixed(1)}`}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <strong>Minute: {String(addMinute).padStart(2, '0')}</strong>
            {isDerate ? (
              <>
                <strong>Derate:</strong>
                <InputNumber
                  value={derate}
                  onChange={setDerate}
                  min={0}
                  style={{ width: 100 }}
                  disabled={saving}
                />
                <strong>AvailCap:</strong>
                <InputNumber
                  value={computedAvailCap}
                  disabled
                  style={{ width: 100 }}
                />
              </>
            ) : (
              <>
                <strong>AvailCap:</strong>
                <InputNumber
                  value={isZeroCap ? 0 : addAvailCap}
                  onChange={setAddAvailCap}
                  style={{ width: 110 }}
                  disabled={isZeroCap || saving}
                />
              </>
            )}
          </div>
          <hr style={{ margin: '10px 0', borderColor: '#f0f0f0' }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 12,
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {!isZeroCap && previewWeightedSum != null && (
                <>
                  <span>
                    <span style={{ color: '#8c8c8c' }}>New Weighted: </span>
                    <span style={{ fontWeight: 600 }}>
                      {previewWeightedSum.toFixed(1)}
                    </span>
                  </span>
                  {previewUnalloc != null && (
                    <span>
                      <span style={{ color: '#8c8c8c' }}>
                        Unallocated after add:{' '}
                      </span>
                      <span
                        style={{
                          fontWeight: 600,
                          color:
                            Math.round(previewUnalloc * 10) / 10 < 0
                              ? '#f5222d'
                              : '#52c41a',
                        }}
                      >
                        {previewUnalloc.toFixed(1)}
                      </span>
                    </span>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Button
                type="primary"
                onClick={handleAdd}
                disabled={
                  saving ||
                  monthlyLockStatus === 'ML' ||
                  (!isZeroCap &&
                    previewUnalloc != null &&
                    Math.round(previewUnalloc * 10) / 10 < 0)
                }
                loading={saving}
              >
                Add
              </Button>
              <Button
                onClick={() => {
                  setAddMinute(0)
                  setAddAvailCap(null)
                  setAddError(null)
                  onClose()
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
          {addError && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#f5222d' }}>
              {addError}
            </div>
          )}
        </div>
      )}

      <MoveEventModal
        visible={moveEvent != null}
        onClose={() => setMoveEvent(null)}
        event={moveEvent}
        eventTypes={eventTypes}
        causeCodeList={causeCodeList}
        onMove={handleMove}
        moving={moving}
      />
    </Modal>
  )
}

export default HourEventsModal
